import React, { useState } from 'react';
import { ArrowUpRight, ArrowRight, ChevronLeft, ChevronRight, MapPin, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import {
  CONCERTS,
  GALLERY,
  TICKET_TIERS,
  EVENTS_PAGE,
  IMAGES,
  SEO_META,
} from '../mock';
import Reveal from '../components/Reveal';
import Booking from '../components/Booking';
import { postEnquiry } from '../api';
import { logError } from '../lib/logger';
import Seo from '../components/Seo';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';

const PAST = CONCERTS.map((c, i) => ({ ...c, image: GALLERY[i % GALLERY.length].image }));

const ticketClass =
  'group/btn inline-flex items-center gap-2.5 border border-white/30 px-6 py-3 text-[10px] tracking-[0.25em] text-white transition-colors duration-300 hover:border-gold hover:text-gold';

const ticketIcon = (
  <ArrowUpRight
    size={12}
    className="transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5"
  />
);

const EventRow = ({ event, onTickets, delay }) => (
  <Reveal delay={delay}>
    <div className="group flex flex-wrap items-center gap-x-8 gap-y-4 border-t border-white/10 py-8 transition-colors duration-300 hover:bg-white/[0.02]">
      <div className="min-w-[72px]">
        <p className="font-serif text-4xl font-light leading-none text-white md:text-5xl">
          {event.day}
        </p>
        <p className="mt-2.5 text-[9px] tracking-[0.3em] text-neutral-500">{event.month}</p>
      </div>
      <div className="min-w-[180px] flex-1">
        <h3 className="font-serif text-2xl font-light tracking-[0.03em] text-white transition-colors duration-300 group-hover:text-gold md:text-[1.7rem]">
          {event.title}
        </h3>
        <p className="mt-1.5 flex items-center gap-1.5 text-xs tracking-[0.05em] text-neutral-500">
          <MapPin size={11} className="text-gold" /> {event.venue}
        </p>
      </div>
      {event.ticketUrl ? (
        <a
          href={event.ticketUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`event-tickets-${event.id}`}
          className={ticketClass}
        >
          GET TICKETS
          {ticketIcon}
        </a>
      ) : (
        <button
          onClick={() => onTickets(event)}
          data-testid={`event-tickets-${event.id}`}
          className={ticketClass}
        >
          GET TICKETS
          {ticketIcon}
        </button>
      )}
    </div>
  </Reveal>
);

const EventsPage = () => {
  const [ticketEvent, setTicketEvent] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const { hero, upcoming, past } = EVENTS_PAGE;

  const reserveTier = async (tier) => {
    try {
      await postEnquiry('tickets', {
        Event: ticketEvent.title,
        Venue: ticketEvent.venue,
        Date: `${ticketEvent.day} ${ticketEvent.month}`,
        Tier: tier.name,
        Price: tier.price,
      });
    } catch (err) {
      logError('Ticket reservation failed:', err);
      toast.error('Could not reserve tickets', {
        description: 'Please check your connection and try again in a moment.',
      });
      return;
    }
    setTicketEvent(null);
    toast.success('Tickets reserved', {
      description: `${tier.name} · ${ticketEvent.title} — a confirmation email will follow.`,
    });
  };

  const step = (dir) =>
    setLightbox((cur) => (cur === null ? cur : (cur + dir + PAST.length) % PAST.length));
  const active = lightbox !== null ? PAST[lightbox] : null;

  const scrollToBooking = () =>
    document.querySelector('#booking')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <main>
      <Seo {...SEO_META.events} />
      {/* Hero */}
      <section className="relative overflow-hidden bg-ink pt-20">
        <div className="absolute inset-y-0 right-0 w-full lg:w-[58%]">
          <img
            src={IMAGES.live}
            alt="Concert crowd"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/75 to-ink/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-ink/30 lg:hidden" />
        </div>
        <div className="relative z-10 mx-auto max-w-[1400px] px-6 py-16 md:px-10 md:py-24">
          <div className="max-w-xl">
            <Reveal>
              <p className="mb-5 flex items-center gap-4 text-[10px] uppercase tracking-[0.5em] text-gold">
                {hero.label}
                <span className="h-px w-14 bg-gold/50" />
              </p>
            </Reveal>
            <Reveal delay={120}>
              <h1 className="font-serif text-5xl font-light leading-[1.05] tracking-[0.02em] text-white md:text-7xl">
                {hero.titleLines[0]}
              </h1>
            </Reveal>
            <Reveal delay={260}>
              <p className="mt-8 font-serif text-lg font-light leading-relaxed text-neutral-300 md:text-xl">
                {hero.text}
              </p>
            </Reveal>
            <Reveal delay={380}>
              <button
                onClick={scrollToBooking}
                className="group mt-10 inline-flex items-center gap-3 bg-gold px-7 py-3.5 text-[11px] tracking-[0.25em] text-ink transition-colors duration-300 hover:bg-gold-light"
              >
                BOOK KHM
                <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Upcoming events */}
      <section className="bg-ink py-16 md:py-20">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="mb-14 max-w-xl">
            <Reveal>
              <p className="mb-5 text-[10px] uppercase tracking-[0.5em] text-gold">{upcoming.label}</p>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="font-serif text-4xl font-light leading-[1.08] tracking-[0.03em] text-white md:text-6xl">
                {upcoming.titleLines[0]}
                <br />
                {upcoming.titleLines[1]}
              </h2>
            </Reveal>
            <Reveal delay={240}>
              <p className="mt-8 text-base leading-relaxed text-neutral-400">{upcoming.text}</p>
            </Reveal>
          </div>
          <div>
            {upcoming.events.map((event, i) => (
              <EventRow key={event.id} event={event} onTickets={setTicketEvent} delay={i * 90} />
            ))}
            <div className="border-t border-white/10" />
          </div>
        </div>
      </section>

      {/* Past events gallery */}
      <section className="bg-cream py-16 md:py-20">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="mb-14 max-w-xl">
            <Reveal>
              <p className="mb-5 text-[10px] uppercase tracking-[0.5em] text-gold-dark">{past.label}</p>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="font-serif text-4xl font-light leading-[1.08] tracking-[0.03em] text-ink md:text-6xl">
                {past.titleLines[0]}
                <br />
                {past.titleLines[1]}
              </h2>
            </Reveal>
            <Reveal delay={240}>
              <p className="mt-8 text-base leading-relaxed text-ink/60">{past.text}</p>
            </Reveal>
          </div>

          <div className="grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
            {PAST.map((item, i) => (
              <Reveal key={item.city} delay={i * 110}>
                <button
                  onClick={() => setLightbox(i)}
                  className="group relative block w-full overflow-hidden text-left"
                  aria-label={`View ${item.city} concert`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={item.image}
                      alt={`${item.city} — ${item.venue}`}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/10 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-5">
                      <p className="font-serif text-2xl tracking-[0.05em] text-white">{item.city}</p>
                      <p className="mt-1 text-[10px] tracking-[0.15em] text-neutral-300">
                        {item.venue}
                      </p>
                    </div>
                    <span className="absolute right-4 top-4 flex items-center gap-1.5 bg-ink/60 px-3 py-1.5 text-[9px] tracking-[0.2em] text-gold backdrop-blur-sm">
                      <CalendarDays size={10} /> {item.date.toUpperCase()}
                    </span>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Book Kanwal — same form as homepage */}
      <Booking />

      {/* Ticket dialog */}
      <Dialog open={!!ticketEvent} onOpenChange={(o) => !o && setTicketEvent(null)}>
        <DialogContent className="border-white/10 bg-ink text-neutral-200 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light tracking-[0.05em] text-white">
              {ticketEvent?.title}
            </DialogTitle>
            <DialogDescription className="text-[10px] uppercase tracking-[0.35em] text-gold">
              {ticketEvent ? `${ticketEvent.day} ${ticketEvent.month} · ${ticketEvent.venue}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            {TICKET_TIERS.map((tier) => (
              <button
                key={tier.id}
                onClick={() => reserveTier(tier)}
                className="group flex w-full items-center justify-between border border-white/15 px-5 py-4 text-left transition-colors duration-300 hover:border-gold"
              >
                <span>
                  <span className="block text-xs tracking-[0.2em] text-white transition-colors duration-300 group-hover:text-gold">
                    {tier.name}
                  </span>
                  <span className="mt-1 block text-[10px] tracking-[0.1em] text-neutral-500">
                    {tier.price}
                  </span>
                </span>
                <ArrowUpRight
                  size={14}
                  className="text-neutral-500 transition-colors duration-300 group-hover:text-gold"
                />
              </button>
            ))}
          </div>
          <p className="pt-1 text-[10px] leading-relaxed tracking-[0.05em] text-neutral-600">
            Preview build — reservations are stored locally and will connect to a ticketing partner
            in the full version.
          </p>
        </DialogContent>
      </Dialog>

      {/* Past events lightbox */}
      <Dialog open={lightbox !== null} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="border-white/10 bg-ink p-0 text-neutral-200 sm:max-w-3xl">
          <DialogTitle className="sr-only">{active ? `${active.city} concert` : 'Gallery'}</DialogTitle>
          <DialogDescription className="sr-only">Past events lightbox</DialogDescription>
          {active && (
            <div>
              <div className="relative aspect-[4/3] w-full overflow-hidden sm:aspect-[16/10]">
                <img
                  src={active.image}
                  alt={`${active.city} — ${active.venue}`}
                  className="h-full w-full object-cover"
                />
                <button
                  onClick={() => step(-1)}
                  aria-label="Previous image"
                  className="absolute left-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-ink/50 text-white backdrop-blur-sm transition-colors duration-300 hover:border-gold hover:text-gold"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  onClick={() => step(1)}
                  aria-label="Next image"
                  className="absolute right-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-ink/50 text-white backdrop-blur-sm transition-colors duration-300 hover:border-gold hover:text-gold"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
              <div className="flex items-center justify-between px-6 py-5">
                <div>
                  <p className="text-[9px] tracking-[0.35em] text-gold">{active.date.toUpperCase()}</p>
                  <p className="mt-1 font-serif text-xl tracking-[0.06em] text-white">
                    {active.city}
                    <span className="ml-3 text-base italic text-neutral-400">{active.venue}</span>
                  </p>
                </div>
                <p className="text-[10px] tracking-[0.25em] text-neutral-500">
                  {String(lightbox + 1).padStart(2, '0')} / {String(PAST.length).padStart(2, '0')}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default EventsPage;
